import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Save,
  Close,
  CleaningServices,
} from '@mui/icons-material';
import api from '../services/api';
import { Store } from '../types';

interface MinMaxRecord {
  id: number;
  store_id: number;
  manufacturer_id: number;
  store_name: string;
  manufacturer_name: string;
  medicine_name: string;
  pack_size: string;
  current_stock: number;
  min: number | null;
  max: number | null;
  deactivate: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

interface Manufacturer {
  id: number;
  manufacturer_name: string;
  medicine_name: string;
  pack_size: string;
}

interface MinMaxManagementProps {
  selectedStore: Store | null;
}

interface EditingCell {
  recordId: number;
  field: 'min' | 'max';
  value: string;
}

const MinMaxManagement: React.FC<MinMaxManagementProps> = ({ selectedStore }) => {
  const [records, setRecords] = useState<MinMaxRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [savingCellId, setSavingCellId] = useState<number | null>(null);
  
  // Add item dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [loadingManufacturers, setLoadingManufacturers] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState<Manufacturer | null>(null);
  const [newMin, setNewMin] = useState<string>('');
  const [newMax, setNewMax] = useState<string>('');
  const [newDeactivate, setNewDeactivate] = useState(false);
  const [addingItem, setAddingItem] = useState(false);

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<MinMaxRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Cleanup orphaned records
  const [cleanupLoading, setCleanupLoading] = useState(false);

  // Fetch records when store changes
  useEffect(() => {
    if (selectedStore) {
      fetchRecords();
    }
  }, [selectedStore]);

  const fetchRecords = async () => {
    if (!selectedStore) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/utilities/item-min-max-all?store_id=${selectedStore.id}`);
      setRecords(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching min-max records:', err);
      setError(err.response?.data?.message || 'Failed to fetch records');
    } finally {
      setLoading(false);
    }
  };

  const fetchManufacturers = async () => {
    setLoadingManufacturers(true);
    try {
      const response = await api.get('/lookup/manufacturers');
      setManufacturers(response.data || []);
    } catch (err) {
      console.error('Error fetching manufacturers:', err);
    } finally {
      setLoadingManufacturers(false);
    }
  };

  const handleOpenAddDialog = () => {
    setAddDialogOpen(true);
    fetchManufacturers();
    setSelectedManufacturer(null);
    setNewMin('');
    setNewMax('');
    setNewDeactivate(false);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setSelectedManufacturer(null);
    setNewMin('');
    setNewMax('');
    setNewDeactivate(false);
  };

  const handleAddItem = async () => {
    if (!selectedStore || !selectedManufacturer) return;

    setAddingItem(true);
    setError(null);

    try {
      await api.post('/utilities/item-min-max', {
        store_id: selectedStore.id,
        manufacturer_id: selectedManufacturer.id,
        min: newMin ? parseInt(newMin) : null,
        max: newMax ? parseInt(newMax) : null,
        deactivate: newDeactivate ? 'true' : 'false',
      });

      handleCloseAddDialog();
      fetchRecords();
    } catch (err: any) {
      console.error('Error adding item:', err);
      setError(err.response?.data?.message || 'Failed to add item');
    } finally {
      setAddingItem(false);
    }
  };

  const handleCellClick = (recordId: number, field: 'min' | 'max', currentValue: number | null) => {
    setEditingCell({
      recordId,
      field,
      value: currentValue?.toString() || '',
    });
  };

  const handleCellChange = (value: string) => {
    if (editingCell) {
      setEditingCell({ ...editingCell, value });
    }
  };

  const handleCellSave = async () => {
    if (!editingCell) return;

    setSavingCellId(editingCell.recordId);
    setError(null);

    try {
      const updateData: any = {};
      updateData[editingCell.field] = editingCell.value ? parseInt(editingCell.value) : null;

      await api.put(`/utilities/item-min-max/${editingCell.recordId}`, updateData);

      // Update local state
      setRecords(records.map(record =>
        record.id === editingCell.recordId
          ? { ...record, [editingCell.field]: updateData[editingCell.field] }
          : record
      ));

      setEditingCell(null);
    } catch (err: any) {
      console.error('Error updating cell:', err);
      setError(err.response?.data?.message || 'Failed to update value');
    } finally {
      setSavingCellId(null);
    }
  };

  const handleCellBlur = () => {
    if (editingCell) {
      handleCellSave();
    }
  };

  const handleCellKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCellSave();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  const handleDeactivateToggle = async (recordId: number, currentValue: string) => {
    setSavingCellId(recordId);
    setError(null);

    try {
      const newValue = currentValue === 'true' ? 'false' : 'true';
      await api.put(`/utilities/item-min-max/${recordId}`, {
        deactivate: newValue,
      });

      // Update local state
      setRecords(records.map(record =>
        record.id === recordId
          ? { ...record, deactivate: newValue }
          : record
      ));
    } catch (err: any) {
      console.error('Error toggling deactivate:', err);
      setError(err.response?.data?.message || 'Failed to update deactivate status');
    } finally {
      setSavingCellId(null);
    }
  };

  const handleOpenDeleteDialog = (record: MinMaxRecord) => {
    setRecordToDelete(record);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRecordToDelete(null);
  };

  const handleDeleteRecord = async () => {
    if (!recordToDelete) return;

    setDeleting(true);
    setError(null);

    try {
      await api.delete(`/utilities/item-min-max/${recordToDelete.id}`);
      setRecords(records.filter(r => r.id !== recordToDelete.id));
      handleCloseDeleteDialog();
    } catch (err: any) {
      console.error('Error deleting record:', err);
      setError(err.response?.data?.message || 'Failed to delete record');
    } finally {
      setDeleting(false);
    }
  };

  const handleCleanupOrphaned = async () => {
    if (!window.confirm('This will remove all min-max records for items that no longer exist. Continue?')) {
      return;
    }

    setCleanupLoading(true);
    setError(null);

    try {
      const response = await api.delete('/utilities/item-min-max/cleanup-orphaned');
      
      if (response.data.success) {
        alert(`Successfully cleaned up ${response.data.count} orphaned records`);
        fetchRecords(); // Refresh the list
      }
    } catch (err: any) {
      console.error('Error cleaning up orphaned records:', err);
      setError(err.response?.data?.message || 'Failed to clean up orphaned records');
    } finally {
      setCleanupLoading(false);
    }
  };

  const renderEditableCell = (record: MinMaxRecord, field: 'min' | 'max') => {
    const isEditing = editingCell?.recordId === record.id && editingCell?.field === field;
    const isSaving = savingCellId === record.id;
    const value = record[field];

    if (isEditing) {
      return (
        <TextField
          autoFocus
          size="small"
          type="number"
          value={editingCell.value}
          onChange={(e) => handleCellChange(e.target.value)}
          onBlur={handleCellBlur}
          onKeyDown={handleCellKeyDown}
          disabled={isSaving}
          sx={{ width: '80px' }}
        />
      );
    }

    return (
      <Box
        onClick={() => handleCellClick(record.id, field, value)}
        sx={{
          cursor: 'pointer',
          padding: '8px',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {value ?? '-'}
        {!isSaving && <Edit sx={{ fontSize: 14, opacity: 0.5 }} />}
        {isSaving && <CircularProgress size={14} />}
      </Box>
    );
  };

  if (!selectedStore) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info">Please select a store from the top bar to view Min-Max records.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Min-Max Management - {selectedStore.store_name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Remove records for deleted items">
            <Button
              variant="outlined"
              color="warning"
              startIcon={cleanupLoading ? <CircularProgress size={20} /> : <CleaningServices />}
              onClick={handleCleanupOrphaned}
              disabled={cleanupLoading}
            >
              Cleanup
            </Button>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenAddDialog}
          >
            Add New Item
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : records.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No min-max records found for this store. Click "Add New Item" to create one.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Manufacturer Name</TableCell>
                <TableCell>Medicine Name</TableCell>
                <TableCell>Pack Size</TableCell>
                <TableCell align="right">Current Stock</TableCell>
                <TableCell align="right">Min</TableCell>
                <TableCell align="right">Max</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell>{record.manufacturer_name}</TableCell>
                  <TableCell>{record.medicine_name}</TableCell>
                  <TableCell>{record.pack_size}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={record.current_stock}
                      size="small"
                      color={
                        record.min && record.current_stock < record.min
                          ? 'error'
                          : record.max && record.current_stock > record.max
                          ? 'warning'
                          : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell align="right">
                    {renderEditableCell(record, 'min')}
                  </TableCell>
                  <TableCell align="right">
                    {renderEditableCell(record, 'max')}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Switch
                        checked={record.deactivate === 'false'}
                        onChange={() => handleDeactivateToggle(record.id, record.deactivate)}
                        disabled={savingCellId === record.id}
                        size="small"
                      />
                      <Typography variant="caption" sx={{ ml: 1 }}>
                        {record.deactivate === 'false' ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(record)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Item Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Min-Max Item</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Autocomplete
              options={manufacturers}
              getOptionLabel={(option) =>
                `${option.manufacturer_name} - ${option.medicine_name} (${option.pack_size})`
              }
              value={selectedManufacturer}
              onChange={(_, newValue) => setSelectedManufacturer(newValue)}
              loading={loadingManufacturers}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Manufacturer/Medicine"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingManufacturers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <TextField
              label="Min Quantity"
              type="number"
              value={newMin}
              onChange={(e) => setNewMin(e.target.value)}
              fullWidth
            />

            <TextField
              label="Max Quantity"
              type="number"
              value={newMax}
              onChange={(e) => setNewMax(e.target.value)}
              fullWidth
            />

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={newDeactivate}
                onChange={(e) => setNewDeactivate(e.target.checked)}
              />
              <Typography>
                {newDeactivate ? 'Start as Inactive' : 'Start as Active'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={addingItem}>
            Cancel
          </Button>
          <Button
            onClick={handleAddItem}
            variant="contained"
            disabled={!selectedManufacturer || addingItem}
            startIcon={addingItem ? <CircularProgress size={20} /> : <Save />}
          >
            Add Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the min-max record for:
          </Typography>
          <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
            {recordToDelete?.manufacturer_name} - {recordToDelete?.medicine_name}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteRecord}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MinMaxManagement;


